import { useCallback, useEffect, useRef, useState } from 'react';
import { I18nProvider } from './i18n/I18nProvider';
import { useI18n } from './i18n/context';
import { MainMenu } from './ui/MainMenu';
import { Toast } from './ui/Toast';
import { Lobby } from './ui/Lobby';
import type { NetHandoff } from './ui/Lobby';
import { NetBattle } from './ui/NetBattle';
import { multiplayerEnabled } from './net/config';
import { Battlefield } from './render/Battlefield';
import { DEFAULT_MAP_ID } from './content/maps';
import type { MapId } from './content/maps';
import { DEFAULT_DIFFICULTY } from './content/difficulties';
import type { DifficultyId } from './content/difficulties';
import { initPwa } from './app/pwa';
import { clearAutosave, readAutosave } from './app/storage';
import type { SaveSlot } from './app/storage';
import { SAVE_VERSION } from './sim/serialize';

type Screen = 'menu' | 'game' | 'lobby' | 'netGame';

function Shell() {
    const { t } = useI18n();
    const [screen, setScreen] = useState<Screen>('menu');
    /*
     * המסירה מהלובי לקרב. היא מחזיקה **תעבורה פתוחה**, ולכן היא נשמרת פעם אחת
     * ונזרקת ביציאה — ולא נבנית מחדש ברינדור.
     */
    const [handoff, setHandoff] = useState<NetHandoff | null>(null);
    const [saveSlot, setSaveSlot] = useState<SaveSlot | null>(null);
    const [resumeFrom, setResumeFrom] = useState<SaveSlot | null>(null);
    const [newMapId, setNewMapId] = useState<MapId>(DEFAULT_MAP_ID);
    const [difficulty, setDifficulty] = useState<DifficultyId>(DEFAULT_DIFFICULTY);
    /*
     * מונה שמאלץ קרב חדש על אותה מפה ובאותו קושי. ה-`key` של Battlefield הוא
     * המנגנון שכבר קיים לבניית מנוע חדש, ו"שחק שוב" הוא בדיוק אותו דבר עם אותם
     * פרמטרים — ולכן הוא צריך מרכיב שמשתנה.
     */
    const [matchNonce, setMatchNonce] = useState(0);
    /*
     * כמה שחקנים בקרב. **מספר ולא "מצב"**: שלושה יריבים הם `free-for-all` —
     * כל אחד נגד כולם, כולל זה נגד זה — וזו התנהגות שנופלת מעצמה מ"אחרון
     * שנותר" ומכך שכל AI תוקף את השכן הקרוב לו.
     */
    const [playerCount, setPlayerCount] = useState(2);
    /*
     * ה-seed של הקרב. **מוגרל כאן ולא בסימולציה** — הקוד הטהור אינו נוגע
     * ב-`Math.random`, וזה בדיוק מה שמאפשר לו להישאר דטרמיניסטי. מכאן הוא נכנס
     * למצב, לשמירה ול-hash, ומוצג ב-F3 — כך שדיווח באג מהטלפון ניתן לשחזור
     * מדויק, וזו גם אבן הפינה של שיתוף replay בהמשך.
     */
    const [seed, setSeed] = useState(() => Math.floor(Math.random() * 0xffffffff) >>> 0);
    const rollSeed = useCallback(() => setSeed(Math.floor(Math.random() * 0xffffffff) >>> 0), []);
    const [needRefresh, setNeedRefresh] = useState(false);
    /** נדחה **לקרב הנוכחי בלבד** — בתפריט הבאנר חוזר תמיד. */
    const [dismissedUpdate, setDismissedUpdate] = useState(false);
    const [offlineReady, setOfflineReady] = useState(false);
    const updateRef = useRef<((reloadPage?: boolean) => Promise<void>) | null>(null);

    useEffect(() => {
        updateRef.current = initPwa({
            onNeedRefresh: () => setNeedRefresh(true),
            onOfflineReady: () => setOfflineReady(true),
        });
    }, []);

    const refreshSave = useCallback(async () => {
        const slot = await readAutosave();
        // שמירה מגרסה אחרת אינה מוצגת כ"המשך משחק": עדיף להודות שאין, מאשר
        // להציע כפתור שיזרוק שגיאה בלחיצה.
        if (slot && slot.game.version !== SAVE_VERSION) {
            await clearAutosave();
            setSaveSlot(null);
            return;
        }
        setSaveSlot(slot);
    }, []);

    useEffect(() => {
        if (screen === 'menu') {
            void refreshSave();
            // חזרה לתפריט מבטלת את הדחייה: זה הרגע שבו עדכון לא עולה לאף אחד.
            setDismissedUpdate(false);
        }
    }, [screen, refreshSave]);

    /*
     * יציב בכוונה: `Lobby` תולה בו אפקט, ו-callback שנבנה מחדש בכל רינדור היה
     * מריץ את המסירה שוב — כלומר מנסה לקחת בעלות על תעבורה שכבר נמסרה.
     */
    const startNet = useCallback((next: NetHandoff) => {
        setHandoff(next);
        setScreen('netGame');
    }, []);

    const leaveNet = useCallback(() => {
        // ‏`NetBattle.leave` כבר סגר את התעבורה. כאן רק זורקים את ההפניה אליה.
        setHandoff(null);
        setScreen('menu');
    }, []);

    return (
        <>
            {screen === 'lobby' && <Lobby onStart={startNet} onBack={() => setScreen('menu')} />}

            {screen === 'netGame' && handoff && (
                <NetBattle
                    // מסירה חדשה = משחק חדש. המפתח מכריח מנוע חדש ולא מצב שנגרר.
                    key={`${handoff.room}-${handoff.localPlayer}`}
                    handoff={handoff}
                    onExit={leaveNet}
                />
            )}

            {screen === 'menu' ? (
                <MainMenu
                    saveSlot={saveSlot}
                    difficulty={difficulty}
                    onDifficulty={setDifficulty}
                    multiplayerAvailable={multiplayerEnabled()}
                    onMultiplayer={() => setScreen('lobby')}
                    onNewGame={(mapId, players) => {
                        setPlayerCount(players);
                        rollSeed();
                        setResumeFrom(null);
                        setNewMapId(mapId);
                        setScreen('game');
                    }}
                    onContinue={() => {
                        if (!saveSlot) return;
                        setResumeFrom(saveSlot);
                        setScreen('game');
                    }}
                />
            ) : screen === 'game' ? (
                <Battlefield
                    // המפתח מכריח בנייה מחדש של המנוע בין "משחק חדש" ל"המשך משחק",
                    // כדי שמשחק חדש לא יירש בטעות מצב טעון.
                    key={
                        resumeFrom
                            ? `resume-${resumeFrom.savedAtMs}`
                            : `new-${newMapId}-${difficulty}-${matchNonce}-${seed}-${playerCount}`
                    }
                    mapId={(resumeFrom?.game.mapId as MapId | undefined) ?? newMapId}
                    // בהמשך משחק הקושי מגיע מהמצב השמור; כאן הוא רק ערך פתיחה.
                    difficulty={difficulty}
                    seed={resumeFrom?.game.seed ?? seed}
                    playerCount={resumeFrom ? resumeFrom.game.players.length : playerCount}
                    resumeFrom={resumeFrom?.game}
                    onExit={() => setScreen('menu')}
                    onRestart={() => {
                        /*
                         * **קודם מקבעים את המפה, ורק אז מנקים את `resumeFrom`.**
                         * ‏`mapId` נגזר מ-`resumeFrom ?? newMapId`, ולכן ניקוי לבדו
                         * החזיר את המפה האחרונה שנבחרה בתפריט — כלומר "שחק שוב"
                         * אחרי "המשך משחק" פתח קרב על מפה אחרת לגמרי.
                         */
                        if (resumeFrom) setNewMapId(resumeFrom.game.mapId as MapId);
                        // **קרב חדש = קוביות חדשות.** בלי זה "שחק שוב" הוא אותו
                        // קרב בדיוק, וזה מה שהיה עד עכשיו.
                        rollSeed();
                        setResumeFrom(null);
                        setMatchNonce((value) => value + 1);
                    }}
                />
            ) : null}

            {/*
                **הבאנר לא נסגר לתמיד.**

                היה לו "אחר כך" שהעלים אותו **ולא היה שום דרך לחזור אליו** — כלומר
                מי שלחץ עליו פעם אחת נשאר על גרסה ישנה בלי לדעת. זה קרה בפועל:
                בדיקה ב-staging הגישה חבילה מלפני עשרים קומיטים, ובר בדק תיקונים
                שכבר היו בקוד ולא היו אצלו.

                עכשיו: בתוך קרב הוא מתכווץ לשבב קטן שאפשר לדחות, אבל **הוא חוזר
                בכל חזרה לתפריט** — שם עדכון לא עולה לאף אחד באמצע משהו.
            */}
            {needRefresh && (screen === 'menu' || !dismissedUpdate) && (
                <Toast
                    variant="update"
                    text={t('update_title')}
                    actionLabel={t('update_action')}
                    onAction={() => {
                        setNeedRefresh(false);
                        void updateRef.current?.(true);
                    }}
                    dismissLabel={screen === 'menu' ? undefined : t('update_dismiss')}
                    onDismiss={screen === 'menu' ? undefined : () => setDismissedUpdate(true)}
                />
            )}
            {!needRefresh && offlineReady && (
                <Toast
                    text={t('offline_ready')}
                    dismissLabel={t('update_dismiss')}
                    onDismiss={() => setOfflineReady(false)}
                />
            )}
        </>
    );
}

export function App() {
    return (
        <I18nProvider>
            <Shell />
        </I18nProvider>
    );
}
